import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getBrands, getCountries } from 'api';
import { extractValidLocale } from 'locales';

export const DataContext = React.createContext({} as any);

export function useCreateDataContext() {
  const [brands, setBrands] = useState<any>({ data: undefined, error: undefined });
  const [countries, setCountries] = useState<any>({ data: undefined, error: undefined });
  const [locale, setLocale] = useState('en');

  const fetch = useCallback((command: Function, toSet: Function) => {
    return async (...args: any) => {
      try {
        const results = await command(...args);
        toSet({
          data: results,
          error: undefined,
        });
      } catch (err) {
        console.error({ err }, 'failed to fetch data');
        toSet({
          data: undefined,
          error: err,
        });
      }
    };
  }, []);

  useEffect(() => {
    const locale = extractValidLocale(navigator.languages || []);
    setLocale(locale);
  }, [setLocale]);

  const dataContext = useMemo(() => ({
    brands,
    countries,
    fetchBrands: fetch(getBrands, setBrands),
    fetchCountries: fetch(getCountries, setCountries),
    locale,
    setLocale,
  }), [
    fetch,
    brands,
    countries,
    setBrands,
    setCountries,
    locale,
    setLocale,
  ]);

  return dataContext;
}

export function DataContextProvider({ children }: { children: JSX.Element }): JSX.Element {
  const dataContext = useCreateDataContext();

  return (
    <DataContext.Provider value={dataContext}>
      {children}
    </DataContext.Provider>
  );
}

export function useDataContext() {
  return useContext(DataContext);
}
